// Which of WeightTons/PowerHp/ReachMeters/EngineHoursAtListing actually make
// sense for a given machinery category, and whether they're required, merely
// optional, or don't apply at all (hidden) -- e.g. a skid steer has no boom,
// so ReachMeters is meaningless for it, while for a scissor lift ReachMeters
// (its working height) is the one number that matters most.
export type MachinerySpecRequirement = 'required' | 'optional' | 'hidden';

export interface MachinerySpecConfig {
  weightTons: MachinerySpecRequirement;
  powerHp: MachinerySpecRequirement;
  reachMeters: MachinerySpecRequirement;
  engineHoursAtListing: MachinerySpecRequirement;
}

// Keyed by MachineryType.Slug (see ITaxonomy.ts) -- only this launch's
// original 4 focus types have a specific table -- anything else (including
// no category picked yet, or any of the taxonomy's other ~79 types) falls
// back to DEFAULT_SPEC_CONFIG below, same as today's behaviour.
const SPEC_CONFIG_BY_CATEGORY: Record<string, MachinerySpecConfig> = {
  'excavadora-de-cadenas': { weightTons: 'required', powerHp: 'required', reachMeters: 'optional', engineHoursAtListing: 'optional' },
  'plataforma-elevadora-articulada-telescopica-tijera': { weightTons: 'optional', powerHp: 'optional', reachMeters: 'required', engineHoursAtListing: 'optional' },
  'minicargadora-skid-steer': { weightTons: 'required', powerHp: 'required', reachMeters: 'hidden', engineHoursAtListing: 'optional' },
  'dumper-articulado': { weightTons: 'required', powerHp: 'optional', reachMeters: 'hidden', engineHoursAtListing: 'optional' },
};

const DEFAULT_SPEC_CONFIG: MachinerySpecConfig = {
  weightTons: 'optional',
  powerHp: 'optional',
  reachMeters: 'optional',
  engineHoursAtListing: 'optional',
};

/**
 * Every spec hidden. Nobody listing a lawnmower knows its operating weight
 * in tonnes or its reach in metres, and a form that asks looks like it was
 * built for somebody else -- which, until the light-equipment types existed,
 * it was.
 */
const LIGHT_EQUIPMENT_SPEC_CONFIG: MachinerySpecConfig = {
  weightTons: 'hidden',
  powerHp: 'hidden',
  reachMeters: 'hidden',
  engineHoursAtListing: 'hidden',
};

/**
 * `isLightEquipment` comes from the selected type itself (see
 * ITaxonomyType), not from a question put to the host, and it wins over any
 * per-category table below: a curated light type has no business being
 * asked for hydraulic figures whatever else is configured.
 */
export const getMachinerySpecConfig = (category?: string, isLightEquipment?: boolean): MachinerySpecConfig => {
  if (isLightEquipment) return LIGHT_EQUIPMENT_SPEC_CONFIG;
  return (category && SPEC_CONFIG_BY_CATEGORY[category]) || DEFAULT_SPEC_CONFIG;
};
